import React, { useEffect, useMemo, useState } from "react";

import { Card, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import EmptyCardDataOperationsDebtPayments from "assets/images/EmptyCardDataOperationsDebtPayments.png";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { PaymentConfirmationDrawer } from "components/Deal-amortization/PaymentConfirmationDrawer";
import { EmptyCard } from "components/Empty-card/EmptyCard";
import { EMPTY_CARD_CONTENT } from "constants/empty-card";
import { useActionHook, useNavigationHooks, useUrlHooks } from "hooks";
import { isEqual } from "lodash";
import { useDateFormatSelect, useDebtActions } from "store";
import { CustomPayment, DateRange, DealBase, ExpectedPaymentRow } from "store/types";
import { formatDate } from "utils";
import rmEmptyFields from "utils/rm-empty-fields";

import { Filters } from "./Filters";
import { TableRow as ExpectedPaymentTableRow } from "./TableRow";

export type FiltersState = {
  assetClasses: { id: number; title: string }[];
  deals: DealBase[];
  date: DateRange | null;
};

const defaultFiltersState: FiltersState = {
  assetClasses: [],
  deals: [],
  date: null
};

const SERVER_DATE_FORMAT = "YYYY-MM-DD";

export const ExpectedPayments: React.FC = () => {
  const { getExpectedPayments } = useDebtActions();
  const dateFormat = useDateFormatSelect();
  const { queryParams } = useUrlHooks();
  const { setParams, removeParams } = useNavigationHooks();

  const {
    action,
    loading,
    data = { table: { rows: [], columns: [] }, custom_payments: [] }
  } = useActionHook(getExpectedPayments);

  const [filtersState, setFiltersState] = useState<FiltersState>(() => {
    const dataOperations = queryParams.dataOperations as string | undefined;
    if (dataOperations) {
      try {
        return JSON.parse(dataOperations) as FiltersState;
      } catch {
        // fall through
      }
    }
    return defaultFiltersState;
  });

  // Filters → URL
  useEffect(() => {
    if (isEqual(filtersState, defaultFiltersState)) {
      removeParams(["dataOperations"]);
    } else {
      setParams({ search: { dataOperations: JSON.stringify(filtersState) } });
    }
  }, [filtersState]);

  // URL → filters when navigating between bookmarks while staying on this page
  useEffect(() => {
    const dataOperations = queryParams.dataOperations as string | undefined;
    if (!dataOperations) return;
    try {
      const fromUrl = JSON.parse(dataOperations) as FiltersState;
      setFiltersState((prev) => (isEqual(prev, fromUrl) ? prev : fromUrl));
    } catch {
      // ignore
    }
  }, [queryParams.dataOperations]);
  const [paymentConfirmation, setPaymentConfirmation] = useState<{
    open: boolean;
    row: ExpectedPaymentRow;
    mode: "approve" | "diff";
  } | null>(null);
  useEffect(() => {
    const body: { asset_class_ids?: number[]; deal_ids?: number[]; date?: DateRange | null } = {
      asset_class_ids: filtersState.assetClasses.map((item) => item.id),
      deal_ids: filtersState.deals.map((item) => item.id),
      ...(filtersState.date && {
        date: {
          ...(filtersState.date.from && {
            from: formatDate({ date: filtersState.date.from, format: SERVER_DATE_FORMAT, readFormatHint: dateFormat })
          }),
          ...(filtersState.date.to && {
            to: formatDate({ date: filtersState.date.to, format: SERVER_DATE_FORMAT, readFormatHint: dateFormat })
          })
        }
      })
    };

    action(rmEmptyFields(body));
  }, [filtersState]);

  const resetFilters = () => {
    setFiltersState(defaultFiltersState);
  };

  const handleConfirmPayment = (row: ExpectedPaymentRow) => {
    setPaymentConfirmation({ open: true, row, mode: "approve" });
  };

  const handleActualPayment = (row: ExpectedPaymentRow) => {
    setPaymentConfirmation({ open: true, row, mode: "diff" });
  };

  // Find the custom payment matching the selected row
  const selectedActualPayment = useMemo<CustomPayment | undefined>(() => {
    const row = paymentConfirmation?.row;
    return row
      ? data.custom_payments.find(
          (payment) =>
            payment.date === row.date &&
            (payment.deal_id === row.sub_deal_id || payment.deal_id === row.deal_id) &&
            payment.is_actual
        )
      : undefined;
  }, [paymentConfirmation?.row, data.custom_payments]);

  // Find the custom payment matching the selected row
  const selectedPrepayment = useMemo<CustomPayment | undefined>(() => {
    const row = paymentConfirmation?.row;
    return row
      ? data.custom_payments.find(
          (payment) =>
            payment.date === row.date &&
            (payment.deal_id === row.sub_deal_id || payment.deal_id === row.deal_id) &&
            !payment.is_actual
        )
      : undefined;
  }, [paymentConfirmation?.row]);

  return (
    <Card
      sx={{
        p: "16px",
        flexGrow: 1,
        boxShadow: "0 1px 2px 0 #1212171a !important",
        borderRadius: "8px"
      }}
    >
      <Stack sx={{ gap: "16px", flexGrow: 1, height: "100%" }}>
        <AsyncContentMui isLoading={loading}>
          {!data.table.rows.length ? (
            <EmptyCard
              title={EMPTY_CARD_CONTENT.expectedPayments.title}
              description={{
                text: EMPTY_CARD_CONTENT.expectedPayments.description,
                externalText: EMPTY_CARD_CONTENT.expectedPayments.externalDescription,
                sx: { width: "380px" }
              }}
              image={{
                src: EmptyCardDataOperationsDebtPayments,
                sx: { width: 315, height: 140 }
              }}
              fitPage
            />
          ) : (
            <>
              <Filters state={filtersState} setFilters={setFiltersState} resetFilters={resetFilters} />
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  overflow: "auto",
                  border: "1px solid #DBE8F5",
                  maxHeight: "100%",
                  borderRadius: "8px",
                  "& .MuiTable-root": {
                    borderCollapse: "separate",
                    borderSpacing: 0
                  },
                  "& .MuiTableHead-root .MuiTableCell-root": {
                    backgroundColor: "#F7FAFD",
                    borderBottom: "1px solid #DBE8F5"
                  },
                  "& .MuiTableBody-roo .MuiTableCell-root": {
                    borderBottom: "1px solid #DBE8F5"
                  },
                  "& .MuiTableCell-root": {
                    padding: "6px 16px"
                  }
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">{/* <Checkbox /> */}</TableCell>
                      <TableCell sx={{ width: "40px", padding: "6px", textAlign: "center" }}></TableCell>
                      <TableCell>Deal</TableCell>
                      <TableCell>Sub Deal</TableCell>
                      <TableCell>Date</TableCell>
                      {data.table.columns.map((column) => (
                        <TableCell key={column.key}>{column.label.replace(/\s*Payment\s*/gi, "").trim()}</TableCell>
                      ))}
                      <TableCell>Total</TableCell>
                      <TableCell width={50} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.table.rows.map((row, index) => (
                      <ExpectedPaymentTableRow
                        key={index}
                        row={row}
                        index={index}
                        columns={data.table.columns}
                        onConfirmPayment={handleConfirmPayment}
                        onActualPayment={handleActualPayment}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </AsyncContentMui>

        <PaymentConfirmationDrawer
          open={paymentConfirmation?.open ?? false}
          amortizationRow={paymentConfirmation?.row ?? null}
          dealId={paymentConfirmation?.row.deal_id ?? 0}
          columns={data.table.columns}
          mode={paymentConfirmation?.mode ?? "approve"}
          actualPayment={selectedActualPayment}
          prepayment={selectedPrepayment}
          onClose={(updated) => {
            setPaymentConfirmation(null);
            if (updated) {
              action(
                rmEmptyFields({
                  asset_class_ids: filtersState.assetClasses.map((item) => item.id),
                  deal_ids: filtersState.deals.map((item) => item.id),
                  ...(filtersState.date && {
                    date: {
                      ...(filtersState.date.from && {
                        from: formatDate({
                          date: filtersState.date.from,
                          format: SERVER_DATE_FORMAT,
                          readFormatHint: dateFormat
                        })
                      }),
                      ...(filtersState.date.to && {
                        to: formatDate({
                          date: filtersState.date.to,
                          format: SERVER_DATE_FORMAT,
                          readFormatHint: dateFormat
                        })
                      })
                    }
                  })
                })
              );
            }
          }}
        />
      </Stack>
    </Card>
  );
};
